import * as React from 'react';

import CampaignLearnerStatsModuleRenderer from 'bundles/campaigns/module-renderers/CampaignLearnerStatsModuleRenderer';
import { useUndoCancellationDataForXdp } from 'bundles/subscriptions-common/utils/cancellationUndoUtils';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AssignButtonNotConfigurable from 'bundles/unified-description-page-common/components/admin/AssignButtonNotConfigurable';
import ConsumerCourseEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerCourseEnrollButtonNotConfigurable';
import HeroCourseraPlusOrLiteCta from 'bundles/unified-description-page-common/components/modules/hero/HeroCourseraPlusOrLiteCta';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroLanguageInfo from 'bundles/unified-description-page-common/components/modules/hero/HeroLanguageInfo';
import HeroModule from 'bundles/unified-description-page-common/components/modules/hero/HeroModule';
import HeroPartnerLogo from 'bundles/unified-description-page-common/components/modules/hero/HeroPartnerLogo';
import HeroTotalEnrollmentCount from 'bundles/unified-description-page-common/components/modules/hero/HeroTotalEnrollmentCount';
import HeroCourseDescription from 'bundles/unified-description-page-common/components/modules/hero/course/HeroCourseDescription';
import KeyInformationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalModule';
import LearningObjectives from 'bundles/unified-description-page-common/components/modules/product-overview/LearningObjectives';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import SkillsAndTools from 'bundles/unified-description-page-common/components/modules/product-overview/SkillsAndTools';
import Details from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import { HERO_BACKGROUND_COLOR, MAX_NUMBER_OF_KIM_ITEMS } from 'bundles/unified-description-page-common/constants';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import AboutSubmoduleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/AboutSubmoduleRenderer';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ProductOverviewModuleConfig } from 'bundles/unified-description-page-common/types/moduleConfig';
import { getConsumerCourseKeyInformationItems } from 'bundles/unified-description-page-common/utils/getConsumerKeyInformationItems';
import getCourseProductDetailItems from 'bundles/unified-description-page-common/utils/getCourseProductDetailItems';
import { COURSE_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';

type Props = {
  pageData: ConsumerCoursePageFieldsFragment;
  config?: ProductOverviewModuleConfig;
};

const ProductOverviewModuleRenderer = ({ pageData, config }: Props) => {
  const { navigationLinks } = usePageNavigation();
  const { getSpecializationLink } = usePageInfo();

  const courseraPlusOwnership = useCourseraPlusOwnership();
  const { showPlusMonthlyToAnnualUpgradeButton, isCourseraPlusEligibleProduct } = courseraPlusOwnership ?? {};
  const { showUndoCancellationCTA } = useUndoCancellationDataForXdp({
    productItemId: pageData?.primarySpecialization?.id,
    isCourseraPlusEligibleProduct,
    partnerIds: pageData?.partners?.map(({ id }) => id),
  });
  const showEnrollmentCountOnReviewsItem = showPlusMonthlyToAnnualUpgradeButton || showUndoCancellationCTA;

  const productTitle = getProductTitle(pageData.name);
  const partnersToDisplay = getPartnersForCourse(pageData.partners);
  const { items: keyInformationItems } = getConsumerCourseKeyInformationItems(
    pageData,
    {
      ...config?.keyInfoModule,
    },
    config?.removeLinksAboveFold,
    MAX_NUMBER_OF_KIM_ITEMS,
    showEnrollmentCountOnReviewsItem
  );

  const overviewDescription = <LearningObjectives learningObjectives={pageData.cmlLearningObjectives} />;
  const overviewSkills = (
    <SkillsAndTools skillTags={pageData.skillTags} disableClickablePills={config?.removeLinksAboveFold} />
  );

  const getDetails = () => {
    return <Details detailItems={getCourseProductDetailItems(pageData, config?.productDetailItems || [])} />;
  };

  const getAdditionalInfo = () => {
    if (showEnrollmentCountOnReviewsItem) {
      return null;
    }

    return <HeroTotalEnrollmentCount count={pageData.totalEnrollmentCount} />;
  };

  const getAdditionalCta = () => {
    if (showEnrollmentCountOnReviewsItem) {
      return null;
    }

    return (
      courseraPlusOwnership?.isUserEligibleToSeeCta && (
        <HeroCourseraPlusOrLiteCta
          isSubscribedToCPlusOrLite={courseraPlusOwnership.isSubscribedToCPlusOrLite}
          isSubscribedToCourseraLite={courseraPlusOwnership.isSubscribedToCourseraLite}
          disableCTA={config?.removeLinksAboveFold}
        />
      )
    );
  };

  return (
    <ProductOverviewHorizontalModule
      anchorId={COURSE_NAV_ITEMS.ABOUT}
      heroBackgroundColor={HERO_BACKGROUND_COLOR}
      navigationLinks={navigationLinks}
      hero={
        <HeroModule
          productTitle={productTitle}
          backgroundImage={config?.backgroundImage}
          backgroundImageElement={config?.backgroundImageElement}
          partnerLogo={
            partnersToDisplay && partnersToDisplay.length > 1 ? (
              <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={false} />
            ) : (
              <HeroPartnerLogo image={partnersToDisplay?.[0]?.primaryLogo} partnerName={partnersToDisplay?.[0]?.name} />
            )
          }
          description={
            <React.Fragment>
              {pageData.primarySpecialization && (
                <HeroCourseDescription
                  primarySpecialization={pageData.primarySpecialization}
                  primarySpecializationLink={getSpecializationLink({
                    productSlug: pageData.primarySpecialization.slug,
                    productVariant: pageData.primarySpecialization.productVariant,
                  })}
                  specializations={pageData.specializations}
                />
              )}
              {config?.showHeroLanguageInfo && (
                <HeroLanguageInfo
                  languages={{
                    primaryLanguages: pageData.primaryLanguages,
                    subtitleLanguages: pageData.subtitleLanguages ?? [],
                    translatedLanguages: pageData.translatedLanguages ?? [],
                    dubbedLanguages: pageData.dubbedLanguages ?? [],
                  }}
                />
              )}
            </React.Fragment>
          }
          instructors={
            <HeroInstructors
              instructors={pageData.instructors}
              averageInstructorRating={pageData.averageInstructorRating}
              instructorRatingCount={pageData.instructorRatingCount}
              partners={pageData.partners}
              disableCTA={config?.removeLinksAboveFold}
              displayTopInstructorBadgeInline
            />
          }
          primaryCta={
            <ConsumerCourseEnrollButtonNotConfigurable
              pageData={pageData}
              size="large"
              onlyShowButton={config?.showAssignButton || showEnrollmentCountOnReviewsItem}
            />
          }
          secondaryCta={
            config?.showAssignButton && <AssignButtonNotConfigurable productSlug={pageData.slug} productType="course" />
          }
          additionalInfo={getAdditionalInfo()}
          additionalCta={getAdditionalCta()}
        />
      }
      keyInformation={<KeyInformationHorizontalModule keyInformationItems={keyInformationItems} />}
      learnerStatsModule={<CampaignLearnerStatsModuleRenderer />}
      aboutSubmodule={
        <AboutSubmoduleRenderer
          description={overviewDescription}
          skills={overviewSkills}
          details={getDetails()}
          config={config}
        />
      }
    />
  );
};

export default ProductOverviewModuleRenderer;
